/** Namespaced localStorage access that never throws (private mode, quota). */

const NS = 'geogame';

export function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(`${NS}.${key}`);
  } catch {
    return null;
  }
}

export function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(`${NS}.${key}`, value);
  } catch {
    // Storage is a convenience here: a resume token that cannot be persisted
    // costs a reconnect, not a match.
  }
}

export function clearLocal(key: string): void {
  try {
    window.localStorage.removeItem(`${NS}.${key}`);
  } catch {
    // ignore
  }
}

/**
 * The player's name, or EMPTY if they have never been given one.
 *
 * It used to fabricate `player-s0bu6` on first read and persist it, which had
 * two costs. The small one is that it is an ugly name to be greeted by. The
 * large one is that once written it was indistinguishable from a name the
 * player had chosen, so the client sent it to the gateway as a choice, and the
 * gateway baked it into the identity token — which is how a typed-in name
 * ended up losing to a placeholder in a group lobby.
 *
 * Naming an anonymous player is the SERVER's job (`anonHandle`), and the local
 * practice driver mints from the same generator. Empty is the honest answer
 * here, and `welcome` fills it in for good a moment later.
 */
/**
 * A token's payload, without verifying it.
 *
 * NOT a security check and never treated as one -- the server re-verifies
 * everything. This answers two questions the client is allowed to ask itself:
 * which slot does this belong in, and is it too old to be worth sending.
 */
function peek(token: string): { kind: string | null; exp: number | null; pid: string | null } {
  const dot = token.indexOf('.');
  if (dot <= 0) return { kind: null, exp: null, pid: null };
  try {
    const b64 = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(window.atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')));
    if (typeof parsed !== 'object' || parsed === null) return { kind: null, exp: null, pid: null };
    const { k, exp, pid } = parsed as { k?: unknown; exp?: unknown; pid?: unknown };
    return {
      kind: typeof k === 'string' ? k : null,
      exp: typeof exp === 'number' ? exp : null,
      pid: typeof pid === 'string' ? pid : null,
    };
  } catch {
    return { kind: null, exp: null, pid: null };
  }
}

/**
 * THE ACCOUNT THIS BROWSER IS SIGNED INTO, remembered across reloads.
 *
 * It used to be held only in memory, so every reload showed "Save my progress"
 * to somebody who had signed in ten seconds earlier. Nothing was actually lost
 * -- the identity token survives and is what the gateway trusts -- but a
 * screen that keeps offering to save progress you have already saved is a
 * screen telling you it forgot, and the player has no way to know it did not.
 *
 * PINNED TO THE PLAYER ID, which is what makes it honest rather than merely
 * convenient. Thirty days on, the identity token expires and `welcome` mints a
 * fresh anonymous one; the remembered address would then be sitting beside a
 * player it has nothing to do with, and the screen would claim a sign-in that
 * no longer exists. Matching the id means a replaced identity silently drops
 * the claim instead of lying about it.
 */
export function rememberAccount(email: string, playerId: string): void {
  writeLocal('accountEmail', email);
  writeLocal('accountPlayerId', playerId);
}

export function forgetAccount(): void {
  clearLocal('accountEmail');
  clearLocal('accountPlayerId');
}

/** The signed-in address, or null if this browser is not that player. */
export function rememberedAccount(): { email: string } | null {
  const email = readLocal('accountEmail');
  const forPlayer = readLocal('accountPlayerId');
  if (email === null || forPlayer === null) return null;

  const token = identityToken();
  if (token === null) {
    // No identity at all: nothing to be signed in AS.
    forgetAccount();
    return null;
  }
  const { pid } = peek(token);
  if (pid !== forPlayer) {
    forgetAccount();
    return null;
  }
  return { email };
}

/**
 * Move the old shared key into whichever slot it actually belonged in.
 *
 * WHY THIS IS NOT DEAD CODE ON ARRIVAL. Both slots are filled by the SOCKET,
 * and the socket does not open until a match starts. So without this, every
 * browser that has ever played arrives at the new build with an empty identity
 * and stays that way until its next match -- and a player who signs in during
 * that window is treated as a new device, so the anonymous progress sitting on
 * this browser is never claimed into their account. That is the exact loss
 * accounts exist to prevent, handed out by the fix for it.
 *
 * Placed by KIND rather than assumed: the old key held whichever token arrived
 * last, so it is `id` for somebody who connected and never got matched, and
 * `resume` for everybody else.
 *
 * Runs once. The legacy key is cleared whatever it held, because a value that
 * can no longer be placed is a value nothing will ever read again.
 */
function migrateLegacyToken(): void {
  const legacy = readLocal('resumeToken');
  if (legacy === null) return;
  clearLocal('resumeToken');

  const { kind } = peek(legacy);
  if (kind === 'id') writeLocal('identityToken', legacy);
  else if (kind === 'resume') writeLocal('matchToken', legacy);
  // Anything else was never one of ours, and `welcome` replaces it shortly.
}

/**
 * THE IDENTITY TOKEN. Kind `id`, thirty days, minted by `welcome`.
 *
 * The only thing that proves this browser is a particular player, and so the
 * only token any REST call may authenticate with. Read it through this
 * function rather than by key: the bug it exists to prevent was a call site
 * reaching for the wrong string.
 */
export function identityToken(): string | null {
  migrateLegacyToken();
  return readLocal('identityToken');
}

/**
 * THE MATCH TOKEN. Kind `resume`, ONE HOUR, minted by `matched`.
 *
 * Names a match to rejoin, and is not an identity: it expires long before the
 * identity token does, and a browser holding only this is a browser about to
 * be handed a new player id. Never send it to a REST route -- `deviceOf`
 * verifies kind `id` and rejects it as `bad_device`.
 */
export function matchToken(): string | null {
  return readLocal('matchToken');
}

/**
 * What `hello` should carry: the live match if there is one, else identity.
 *
 * THE EXPIRY CHECK IS THE POINT. `onHello` ignores a token it cannot verify
 * and issues a fresh anonymous identity in its place, which is the right call
 * for a forged token and a quiet disaster for a stale one: the player loses
 * the id their coverage, rivalries and rating hang off. A match token lives an
 * hour, so sending one the morning after a match is exactly how that happens.
 *
 * Dropping it here means the identity token -- thirty days, still good -- is
 * what gets sent instead, and the player comes back as themselves.
 */
export function helloToken(): string | null {
  migrateLegacyToken();
  const match = matchToken();
  if (match !== null) {
    const { exp } = peek(match);
    if (exp === null || exp > Date.now()) return match;
    // Dead weight, and actively harmful to send. Stop carrying it.
    clearLocal('matchToken');
  }
  return identityToken();
}

/**
 * The clock this browser plays on, remembered.
 *
 * REMEMBERED IS THE FEATURE. Somebody who needs a slower clock needs it every
 * time, and asking them to find the setting again on every visit is a worse
 * version of not having it. Standard when nothing has been said, so nobody who
 * has never opened it notices the option exists.
 */
export function persistedPace(): 'standard' | 'relaxed' {
  return readLocal('pace') === 'relaxed' ? 'relaxed' : 'standard';
}

export function persistPace(pace: 'standard' | 'relaxed'): void {
  writeLocal('pace', pace);
}

export function persistedHandle(): string {
  return readLocal('handle') ?? '';
}

/**
 * This browser's memory of a daily it has played.
 *
 * THE MODE IS IN THE KEY, because there are three dailies a day now. Without
 * it, finishing Countries would show that score on the Cities daily and claim
 * a puzzle nobody had opened -- the server enforces per mode and would happily
 * have let them play, so the only thing wrong would have been what the screen
 * said.
 *
 * A courtesy either way: `claimDailyAttempt` is the enforcement, and this is
 * only what we can say without asking.
 */
export function dailyKey(dateKey: string, tier: string, gameMode: string): string {
  return `daily.${dateKey}.${tier}.${gameMode}`;
}

/**
 * Has this browser ever started a match?
 *
 * Drives two onboarding decisions that must NOT be re-asked of a regular:
 * which tier the home screen preselects, and whether the guided first round is
 * offered. Absence of the flag is the only signal we have — there are no
 * accounts — so it is deliberately set on match START rather than on match end.
 * Somebody who bounces mid-match has still seen the game.
 */
export function hasPlayedBefore(): boolean {
  return readLocal('played') === '1';
}

export function markPlayed(): void {
  writeLocal('played', '1');
}

/**
 * The tier the home screen should preselect.
 *
 * A first-time visitor gets EXPLORER, not the middle tier: three pinpoint
 * attempts and a 48px snap radius are the difference between learning the loop
 * and bouncing off it. A returning player gets whatever they last chose.
 */
export function persistedTier(): string | null {
  if (!hasPlayedBefore()) return null;
  return readLocal('tier');
}

export function persistTier(tier: string): void {
  writeLocal('tier', tier);
}

/**
 * The one-shot coaching beats — golden round, first bust — that have already
 * been shown to this browser.
 *
 * ONCE EVER, NOT ONCE PER MATCH, which is why it is here rather than in a ref
 * on the match screen. Both beats exist for the moment a player meets
 * something for the first time; the second time, they know, and a card
 * explaining it again is an interruption during a live round.
 *
 * Same courtesy as everything else in this file: storage is a convenience. If
 * it is unavailable the line shows again, which is a far smaller harm than a
 * player meeting a x9 open-numeric question with no warning.
 */
export function coachBeatSeen(beat: string): boolean {
  return readLocal(`coach.${beat}`) === '1';
}

export function markCoachBeatSeen(beat: string): void {
  writeLocal(`coach.${beat}`, '1');
}

/**
 * Has this browser dismissed the first-run intro without playing it?
 *
 * SEPARATE FROM `played`, and it has to be. `played` is set when a match
 * STARTS, so it cannot speak for somebody who tapped "skip" and never started
 * one — and without this flag that person would be handed the intro again on
 * every visit, forever, which is precisely the nagging the intro is supposed
 * to make unnecessary.
 *
 * The guided round itself stays reachable from the home screen afterwards. A
 * skip is "not now", not "never".
 */
export function introDismissed(): boolean {
  return readLocal('introSkipped') === '1';
}

export function dismissIntro(): void {
  writeLocal('introSkipped', '1');
}
